import React from 'react';

import { useDataAttributes } from '@lumapps/data-attributes';
import { useFormField } from '@lumapps/lumx-forms/hooks/useFormField';
import { BaseInput } from '@lumapps/lumx-forms/types';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { DateTimeZonePickerField, type DateTimeZonePickerFieldProps } from '../DateTimeZonePickerField';
import type { GetSelectedDateTimeZone } from '../DateTimeZonePickerField/utils';

type FormDateTimeZonePickerFieldProps = Omit<
    DateTimeZonePickerFieldProps,
    'onChange' | 'error' | 'timestamp' | 'timeZone'
> &
    BaseInput & { scope: string };

/**
 * A Wrapper around DateTimeZone picker for lumx-forms.
 *
 * @family Forms
 * @param FormDateTimeZonePickerFieldProps
 * @returns FormDateTimeZonePickerField
 */
export const FormDateTimeZonePickerField: React.FC<FormDateTimeZonePickerFieldProps> = ({
    name,
    label,
    isRequired,
    controllerProps,
    scope,
    ...dateTimeZonePickerFieldProps
}) => {
    const { translateKey } = useTranslate();
    const { field, fieldState, valueToUse } = useFormField<{
        timestamp: DateTimeZonePickerFieldProps['timestamp'];
        data?: GetSelectedDateTimeZone;
    }>({
        name,
        label,
        isRequired,
        controllerProps,
    });
    const { get } = useDataAttributes(`${scope}-${name}`);

    return (
        <DateTimeZonePickerField
            {...dateTimeZonePickerFieldProps}
            datePickerProps={{
                placeholder: translateKey(GLOBAL.DATE),
                onClear: () => field.onChange({ timestamp: undefined, data: { ...valueToUse?.data, date: undefined } }),
                clearButtonProps: !dateTimeZonePickerFieldProps.shouldFallbackToCurrentDateTime
                    ? { label: translateKey(GLOBAL.CLEAR) }
                    : undefined,
                ...dateTimeZonePickerFieldProps.datePickerProps,
                isRequired,
                ...get({ element: 'date-picker' }),
            }}
            timePickerProps={{
                placeholder: translateKey(GLOBAL.TIME),
                onClear: () => field.onChange({ timestamp: undefined, data: { ...valueToUse?.data, time: undefined } }),
                hasClearButton: !dateTimeZonePickerFieldProps.shouldFallbackToCurrentDateTime,
                ...dateTimeZonePickerFieldProps.timePickerProps,
                isRequired,
                ...get({ element: 'time-picker' }),
            }}
            timeZonePickerProps={{
                ...dateTimeZonePickerFieldProps.timeZonePickerProps,
                isRequired,
                ...get({ element: 'timezone-picker' }),
            }}
            timestamp={valueToUse?.timestamp}
            timeZone={valueToUse?.data?.timeZone.id}
            onChange={field.onChange}
            error={fieldState.error ? fieldState.error?.message || translateKey(GLOBAL.FIELD_IS_REQUIRED) : undefined}
        />
    );
};
